// src/pages/departments/BusinessAdmin.js
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";

const BusinessAdmin = () => {
  const [selectedStaff, setSelectedStaff] = useState(null);

  const galleryVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: (i) => ({
      opacity: 1,
      scale: 1,
      transition: { delay: i * 0.2, duration: 0.6 },
    }),
  };

  const staff = [
    {
      name: "Dr. Michael Olalekan Adeoti",
      role: "Head of Department",
      img: "/images/hodbam.jpg",
      bio: `Dr. Michael Olalekan Adeoti is a distinguished scholar and administrator 
      with vast experience in management education. He holds a Ph.D. in Human 
      Resource Management from Universiti Utara Malaysia, and has served as a 
      visiting lecturer and project supervisor in several international and local 
      institutions. His leadership in the Department of Business Administration 
      emphasizes academic excellence, innovation, and staff welfare.`,
      email: "adeoti.michael@fedpolybida.edu.ng",
      phone: "+234 803 683 2662",
    },
    {
      name: "Mrs. O. A. Alao",
      role: "Senior Lecturer / Academic Adviser",
      img: "/images/staff-alao.jpg",
      bio: `Mrs. Alao is a passionate lecturer with years of experience guiding 
      students in academic and career development. She specializes in marketing 
      management and business ethics, contributing to curriculum development 
      and mentoring students in entrepreneurship.`,
      email: "alao.olamide@fedpolybida.edu.ng",
    },
    {
      name: "Mr. J. O. Ayeni",
      role: "Lecturer / ND Programme Coordinator",
      img: "/images/staff-ayeni.jpg",
      bio: `Mr. Ayeni brings both academic insight and field experience in business 
      management and administration. His commitment to practical training helps 
      bridge theory and real-world applications for ND students.`,
      email: "ayeni.john@fedpolybida.edu.ng",
    },
    {
      name: "Mrs. F. A. Ojo",
      role: "Lecturer / Research Supervisor",
      img: "/images/staff-ojo.jpg",
      bio: `Mrs. Ojo is a dedicated academic known for her expertise in human 
      resource management and organizational behavior. She mentors students 
      in business research and supports departmental development initiatives.`,
      email: "ojo.funmilayo@fedpolybida.edu.ng",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {/* Hero Banner */}
      <section className="relative h-[300px] md:h-[400px] flex items-center justify-center overflow-hidden">
        <motion.img
          src="/images/department-hero.jpg"
          alt="Department Banner"
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ scale: 1.2, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1 }}
        />
        <div className="absolute inset-0 bg-black/50" />
        <motion.div
          className="relative text-center text-white px-6"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-3xl md:text-5xl font-extrabold mb-3">
            Department of Business Administration and Management
          </h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto">
            Excellence in Teaching, Research, and Leadership Development
          </p>
        </motion.div>
      </section>

      <div className="px-6 md:px-20 py-16">
        {/* History Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">History</h2>
          <p className="text-justify text-gray-600 leading-relaxed">
            The <strong>Department of Business Administration and Management</strong> 
            at the <strong>Federal Polytechnic, Bida</strong>, is one of the most 
            vibrant and innovative departments within the School of Business Studies. 
            Since its inception, it has produced graduates who demonstrate leadership, 
            professionalism, and creativity in various sectors of the economy.
          </p>
          <p className="text-justify text-gray-600 leading-relaxed mt-3">
            The department offers <strong>ND</strong> and <strong>HND</strong> 
            programmes that blend theory with practical exposure, empowering 
            students to thrive in today’s dynamic business environment.
          </p>
        </motion.section>

        {/* Why Study Section */}
        <motion.section
          className="bg-blue-50 shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Why Study Business Administration?
          </h2>
          <p className="text-justify text-gray-600 leading-relaxed mb-3">
            Business Administration equips students with versatile skills in 
            management, leadership, finance, and entrepreneurship. The course 
            prepares graduates for roles in both corporate and self-employed 
            settings, offering flexibility and global relevance.
          </p>
          <ul className="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
            <li>Gain in-demand management and leadership skills.</li>
            <li>Learn through practical experiences and industry exposure.</li>
            <li>Access mentorship from experienced lecturers and business experts.</li>
            <li>Develop entrepreneurial competence and problem-solving capacity.</li>
          </ul>
        </motion.section>

        {/* HOD Section */}
        <motion.section
          className="grid md:grid-cols-3 gap-8 items-center bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="md:col-span-1 flex justify-center">
            <motion.img
              src="/images/hodbam.jpg"
              alt="Dr. Michael Olalekan Adeoti"
              className="w-40 h-40 rounded-full object-cover shadow-lg border-4 border-blue-200"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            />
          </div>
          <div className="md:col-span-2">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Head of Department's Speech
            </h2>
            <p className="text-justify text-gray-600 leading-relaxed mb-3">
              It gives me great pleasure to welcome you to the Department of 
              Business Administration and Management, a hub for innovative learning 
              and practical business leadership. Our department strives to develop 
              graduates who will be the future leaders of industries and enterprises 
              in Nigeria and beyond.
            </p>
            <p className="mt-4 text-blue-700 font-semibold">— Dr. Michael Olalekan Adeoti</p>
            <p className="text-gray-500">Head, Department of Business Administration</p>
          </div>
        </motion.section>

        {/* Meet Our Staff Section */}
        <motion.section
          className="mb-16"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            Meet Our Staff
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
            {staff.map((member, i) => (
              <motion.div
                key={i}
                className="bg-white rounded-2xl shadow-lg p-4 text-center cursor-pointer hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
                onClick={() => setSelectedStaff(member)}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.2, duration: 0.6 }}
              >
                <img
                  src={member.img}
                  alt={member.name}
                  className="w-32 h-32 rounded-full object-cover mx-auto shadow-md mb-4"
                />
                <h3 className="text-lg font-semibold text-gray-800">{member.name}</h3>
                <p className="text-blue-700 text-sm font-medium">{member.role}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Modal Popup */}
        <AnimatePresence>
          {selectedStaff && (
            <motion.div
              className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="bg-white rounded-2xl shadow-2xl max-w-lg w-11/12 p-6 relative"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.4 }}
              >
                <button
                  onClick={() => setSelectedStaff(null)}
                  className="absolute top-3 right-4 text-gray-500 hover:text-red-600 text-2xl font-bold"
                >
                  ×
                </button>
                <div className="text-center">
                  <img
                    src={selectedStaff.img}
                    alt={selectedStaff.name}
                    className="w-32 h-32 mx-auto rounded-full object-cover shadow-lg mb-4"
                  />
                  <h3 className="text-xl font-bold text-gray-800">
                    {selectedStaff.name}
                  </h3>
                  <p className="text-blue-700 font-medium mb-3">
                    {selectedStaff.role}
                  </p>
                  <p className="text-gray-600 text-justify leading-relaxed mb-3">
                    {selectedStaff.bio}
                  </p>
                  {selectedStaff.email && (
                    <p className="text-gray-600">
                      <strong>Email:</strong> {selectedStaff.email}
                    </p>
                  )}
                  {selectedStaff.phone && (
                    <p className="text-gray-600">
                      <strong>Phone:</strong> {selectedStaff.phone}
                    </p>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Gallery Section */}
        <motion.section
          className="mb-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Department Gallery
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {["bam1.jpg", "bam2.jpg", "bam3.jpg", "bam4.jpg"].map((img, i) => (
              <motion.img
                key={i}
                src={`/images/${img}`}
                alt={`Gallery ${i + 1}`}
                className="w-full h-44 md:h-52 object-cover rounded-xl shadow-lg cursor-pointer"
                custom={i}
                variants={galleryVariants}
                initial="hidden"
                whileInView="visible"
                whileHover={{ scale: 1.1 }}
              />
            ))}
          </div>
        </motion.section>
         {/* Contact Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Contact the Department
          </h2>
          <p className="text-gray-600 mb-2">
            <strong>Address:</strong> Federal Polytechnic, Bida, Niger State, Nigeria
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Email:</strong> adeoti.michael@fedpolybida.edu.ng
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Phone:</strong> +234 803 683 2662
          </p>
        </motion.section>

        {/* Map Section */}
        <motion.section
          className="rounded-2xl overflow-hidden shadow-md"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <iframe
            title="Department Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3926.347885951447!2d6.0017768!3d9.0456201!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104967b9cf84f97d%3A0x491c441a9ca3e9e1!2sBusiness%20Administration%20Department!5e0!3m2!1sen!2sng!4v1730683200000!5m2!1sen!2sng"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
          ></iframe>
        </motion.section>
      </div>
    </div>
  );
};

export default BusinessAdmin;
